import { tours } from "@/mocks/homeData";
import { WHATSAPP_LINK } from "@/pages/home/constants";

const TOUR_IMAGE_ALT: Record<string, string> = {
  compartilhado: "Passeio de lancha compartilhado em Capitólio no Lago de Furnas",
  exclusivo: "Passeio de lancha exclusivo e privativo em Capitólio no Lago de Furnas",
};

const TOUR_ANCHOR: Record<string, string> = {
  compartilhado: "passeio-compartilhado",
  exclusivo: "passeio-exclusivo",
};

export default function Services() {
  return (
    <section id="passeio-de-lancha" className="relative py-7 md:py-12 bg-background-50 scroll-mt-16">
      <div className="w-full px-4 md:px-6 lg:px-8">
        <div className="text-center mb-5 md:mb-8 reveal">
          <span className="inline-block text-[11px] md:text-xs font-semibold uppercase tracking-[0.2em] text-accent-700 mb-3">
            Experiências no Lago de Furnas
          </span>
          <h2 className="text-xl md:text-3xl font-heading font-bold text-primary-900 mb-3">
            Passeios de Lancha em Capitólio
          </h2>
          <p className="text-xs md:text-sm text-foreground-600 max-w-xl mx-auto">
            Passeio de lancha compartilhado ou exclusivo (privativo) no Lago de Furnas: escolha a forma de
            viver o Mar de Minas — em grupo ou só com a sua turma.
          </p>
        </div>

        <div className="max-w-4xl mx-auto grid grid-cols-2 gap-3 md:gap-8">
          {tours.map((tour, index) => (
            <article
              key={tour.id}
              id={TOUR_ANCHOR[tour.id]}
              className={`reveal reveal-delay-${index + 1} group relative flex flex-col rounded-2xl overflow-hidden transition-all duration-300 scroll-mt-16 ${
                tour.featured
                  ? "bg-primary-900 border border-accent-500/50"
                  : "bg-background-50 border border-background-200/80 hover:border-accent-500/60"
              }`}
            >
              {tour.featured && (
                <span className="absolute top-2.5 left-2.5 md:top-4 md:left-4 z-10 inline-flex items-center gap-1 rounded-full bg-accent-500 px-2.5 py-1 text-[9px] md:text-[11px] font-bold uppercase tracking-wide text-primary-950 whitespace-nowrap">
                  <i className="ri-medal-fill text-[10px]" />
                  Mais escolhido
                </span>
              )}
              <div className="relative h-28 md:h-52 overflow-hidden">
                <img
                  src={tour.image}
                  alt={TOUR_IMAGE_ALT[tour.id] ?? tour.name}
                  title={`${TOUR_IMAGE_ALT[tour.id] ?? tour.name} | Eco Tour Turismo`}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div
                  className={`absolute inset-0 ${
                    tour.featured
                      ? "bg-gradient-to-t from-primary-950/80 to-transparent"
                      : "bg-gradient-to-t from-primary-950/55 to-transparent"
                  }`}
                />
                <div className="absolute bottom-2.5 left-2.5 right-2.5 md:bottom-4 md:left-4 md:right-auto">
                  <p className="font-heading text-[13px] md:text-2xl font-bold leading-tight text-accent-400">
                    {tour.priceLabel}
                  </p>
                  <p className="text-[9px] md:text-xs font-semibold text-white/80 mt-1 whitespace-nowrap">
                    {tour.priceNote}
                  </p>
                </div>
              </div>

              <div className="flex flex-col flex-1 p-3 md:p-6">
                <div className="flex items-center gap-2 mb-2 md:mb-3">
                  <span
                    className={`w-7 h-7 md:w-9 md:h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                      tour.featured ? "bg-accent-500/20" : "bg-primary-100"
                    }`}
                  >
                    <i
                      className={`${tour.icon} text-sm md:text-lg ${
                        tour.featured ? "text-accent-400" : "text-primary-600"
                      }`}
                    />
                  </span>
                  <h3
                    className={`text-xs md:text-lg font-heading font-bold leading-tight ${
                      tour.featured ? "text-white" : "text-primary-900"
                    }`}
                  >
                    {tour.name}
                  </h3>
                </div>
                <p
                  className={`text-[11px] md:text-sm leading-relaxed mb-3 md:mb-4 ${
                    tour.featured ? "text-white/70" : "text-foreground-600"
                  }`}
                >
                  {tour.description}
                </p>
                <ul className="space-y-1.5 md:space-y-2 mb-4 md:mb-6">
                  {tour.includes.map((item) => (
                    <li
                      key={item}
                      className={`flex items-start gap-1.5 md:gap-2 text-[10px] md:text-sm ${
                        tour.featured ? "text-white/80" : "text-foreground-700"
                      }`}
                    >
                      <span
                        className={`w-3.5 h-3.5 md:w-4 md:h-4 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                          tour.featured ? "bg-accent-500/25" : "bg-accent-100"
                        }`}
                      >
                        <i
                          className={`ri-check-line text-[9px] ${
                            tour.featured ? "text-accent-400" : "text-accent-700"
                          }`}
                        />
                      </span>
                      {item}
                    </li>
                  ))}
                </ul>
                <a
                  href={WHATSAPP_LINK}
                  data-whatsapp-cta
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className={`mt-auto inline-flex items-center justify-center gap-1.5 px-3 md:px-5 py-2.5 rounded-md text-[11px] md:text-xs font-bold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    tour.featured
                      ? "bg-accent-500 text-primary-950 hover:bg-accent-400"
                      : "bg-primary-500 text-white hover:bg-primary-600"
                  }`}
                >
                  <i className="ri-whatsapp-line text-sm md:text-base" />
                  Quero reservar
                </a>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}